/**
 * What the referees concluded.
 *
 * A clean review collapses to a single quiet line — the common case should not
 * compete with the answer for attention. Errata, by contrast, are shown open
 * and prominently, because the reader has already read the thing being
 * corrected.
 */

import { useState } from "react";
import { AlertTriangle, Check, X } from "lucide-react";
import {
  AGENT_BY_ID,
  significantFindings,
  type Finding,
  type RefereeId,
  type Review,
  type Verdict,
} from "../../shared/agents.ts";
import { Markdown } from "../markdown/Markdown.tsx";
import { MathText } from "../markdown/MathText.tsx";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible.tsx";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert.tsx";
import { Badge } from "./ui/badge.tsx";
import { Separator } from "./ui/separator.tsx";
import { cn } from "../lib/utils.ts";

const VERDICT_COPY: Readonly<Record<Verdict, string>> = {
  clean: "Checked — dimensions, limits, algebra, and figures all hold up.",
  "minor-issues": "Checked, with notes worth reading.",
  "errors-found": "Corrections below — part of this answer was wrong.",
};

const VERDICT_MARK: Readonly<Record<Verdict, { icon: typeof Check; fill: string }>> = {
  clean: { icon: Check, fill: "bg-success" },
  "minor-issues": { icon: AlertTriangle, fill: "bg-warning" },
  "errors-found": { icon: X, fill: "bg-destructive" },
};

const VERDICT_BADGE: Readonly<Record<Verdict, string>> = {
  clean: "border-success text-success",
  "minor-issues": "border-warning text-warning",
  "errors-found": "border-destructive text-destructive",
};

const SEVERITY_RULE: Readonly<Record<Finding["severity"], string>> = {
  error: "border-l-destructive",
  warning: "border-l-warning",
  note: "border-l-border-strong",
};

export function ReviewPanel({ review }: { review: Review }) {
  const [open, setOpen] = useState(review.verdict === "errors-found");
  const findings = significantFindings(review);
  const entries = Object.entries(review.reports) as [
    RefereeId,
    Review["reports"][RefereeId],
  ][];

  const mark = VERDICT_MARK[review.verdict];
  const MarkIcon = mark.icon;

  return (
    <section className="mt-6 border-t pt-3 font-sans" aria-label="Referee report">
      <h3 className="sr-only">Referee report</h3>

      {review.errata && (
        <Alert className="mb-3 rounded-l-none border-0 border-l-[3px] border-l-destructive bg-muted">
          <AlertTitle className="text-xs font-semibold tracking-[0.08em] text-destructive uppercase">
            Erratum
          </AlertTitle>
          <AlertDescription className="text-foreground [&_.prose]:text-[0.97em]">
            <Markdown content={review.errata} />
          </AlertDescription>
        </Alert>
      )}

      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger className="flex w-full items-center gap-2 text-left text-sm text-secondary-foreground">
          <span
            aria-hidden="true"
            className={cn(
              "grid size-[17px] shrink-0 place-items-center rounded-full text-background",
              mark.fill,
            )}
          >
            <MarkIcon className="size-2.5" strokeWidth={3} />
          </span>
          <span className="flex-1">{VERDICT_COPY[review.verdict]}</span>
          <span className="text-xs text-primary">
            {open ? "hide detail" : "show detail"}
          </span>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="mt-3 grid gap-4 rounded-md bg-muted p-3 text-sm">
            {entries.map(([id, report]) =>
              report ? (
                <article key={id}>
                  <h4 className="mb-1 flex items-center gap-2 text-sm font-semibold">
                    {AGENT_BY_ID[id].label}
                    <Badge
                      variant="outline"
                      className={cn(
                        "font-normal",
                        VERDICT_BADGE[report.verdict],
                      )}
                    >
                      {report.verdict.replace("-", " ")}
                    </Badge>
                  </h4>
                  <p className="text-secondary-foreground">
                    <MathText children={report.summary} />
                  </p>

                  {report.findings.length > 0 && (
                    <ul className="mt-2 grid gap-3">
                      {report.findings.map((finding, index) => (
                        <li
                          key={index}
                          className={cn(
                            "border-l-2 pl-3",
                            SEVERITY_RULE[finding.severity],
                          )}
                        >
                          {finding.quote && (
                            <q className="mb-1 block font-serif text-muted-foreground italic">
                              <MathText children={finding.quote} />
                            </q>
                          )}
                          <p>
                            <MathText children={finding.issue} />
                          </p>
                          <p>
                            <span className="text-xs tracking-[0.06em] text-muted-foreground uppercase">
                              Fix:
                            </span>{" "}
                            <MathText children={finding.correction} />
                          </p>
                        </li>
                      ))}
                    </ul>
                  )}
                </article>
              ) : null,
            )}

            {findings.length === 0 && (
              <>
                {entries.length > 0 && <Separator />}
                <p className="text-muted-foreground">
                  No referee raised anything that would change the answer.
                </p>
              </>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </section>
  );
}
